
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/context/AuthContext";

export interface AffiliateCode {
  id: string;
  code: string;
  commission_rate: number;
  total_earnings: number;
  total_sales: number;
  is_active: boolean;
  created_at: string;
}

export interface AffiliateSale {
  id: string;
  sale_amount: number;
  commission_amount: number;
  status: string;
  created_at: string;
  customer_user_id: string;
  customer_email?: string;
  customer_name?: string;
}

export const useAffiliateSystem = () => {
  const { user } = useAuth();
  const [affiliateCode, setAffiliateCode] = useState<AffiliateCode | null>(null);
  const [sales, setSales] = useState<AffiliateSale[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  // Buscar código de afiliado existente
  const fetchAffiliateCode = async () => {
    if (!user) return;

    try {
      const { data, error } = await supabase
        .from('affiliate_codes')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') {
        console.error('Erro ao buscar código de afiliado:', error);
        return;
      }

      if (data) {
        setAffiliateCode(data);
      }
    } catch (err) {
      console.error('Erro na busca:', err);
    }
  };

  // Criar novo código de afiliado
  const createAffiliateCode = async () => {
    if (!user) return;

    setIsLoading(true);
    try {
      // Gerar código único
      const { data: codeData, error: codeError } = await supabase
        .rpc('generate_affiliate_code', { user_email: user.email });

      if (codeError) {
        console.error('Erro ao gerar código:', codeError);
        return;
      }

      // Inserir na tabela
      const { data, error } = await supabase
        .from('affiliate_codes')
        .insert({
          user_id: user.id,
          code: codeData,
          commission_rate: 20.00
        })
        .select()
        .single();

      if (error) {
        console.error('Erro ao criar código de afiliado:', error);
        return;
      }

      setAffiliateCode(data);
    } catch (err) {
      console.error('Erro na criação:', err);
    } finally {
      setIsLoading(false);
    }
  };

  // Buscar vendas do afiliado com informações do cliente
  const fetchAffiliateSales = async () => {
    if (!affiliateCode) return;

    try {
      // Buscar vendas básicas primeiro
      const { data: salesData, error: salesError } = await supabase
        .from('affiliate_sales')
        .select('*')
        .eq('affiliate_code_id', affiliateCode.id)
        .order('created_at', { ascending: false });

      if (salesError) {
        console.error('Erro ao buscar vendas:', salesError);
        return;
      }

      // Se não há vendas, definir array vazio
      if (!salesData || salesData.length === 0) {
        setSales([]);
        return;
      }

      // Buscar informações dos clientes
      const customerIds = salesData.map(sale => sale.customer_user_id);
      const { data: profilesData } = await supabase
        .from('profiles')
        .select('id, email, first_name')
        .in('id', customerIds);

      // Mapear os dados para incluir informações do cliente
      const salesWithCustomerInfo = salesData.map(sale => {
        const customerProfile = profilesData?.find(p => p.id === sale.customer_user_id);
        return {
          ...sale,
          customer_email: customerProfile?.email,
          customer_name: customerProfile?.first_name || 'Cliente'
        };
      });

      setSales(salesWithCustomerInfo);
    } catch (err) {
      console.error('Erro na busca de vendas:', err);
    }
  };

  useEffect(() => {
    if (user) {
      fetchAffiliateCode();
    }
  }, [user]);

  useEffect(() => {
    if (affiliateCode) {
      fetchAffiliateSales();
    }
  }, [affiliateCode]);

  return {
    affiliateCode,
    sales,
    isLoading,
    createAffiliateCode,
    refreshData: () => {
      fetchAffiliateCode();
      fetchAffiliateSales();
    }
  };
};
